import { getCurrentTimezone, getCurrentDateFormat, getCurrentTimeFormat, getLocale } from '../config/settings';
import { Timestamp } from 'firebase/firestore';

/**
 * Formats a date string using centralized settings and user's preferred format
 */
export const formatDate = (dateInput: any): string => {
  let date = dateInput;
  if (!date) return '';
  if (date instanceof Timestamp) {
    date = date.toDate();
  }
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const timezone = getCurrentTimezone();
  const locale = getLocale();
  const dateFormat = getCurrentDateFormat();
  
  const year = date.toLocaleDateString(locale, { timeZone: timezone, year: 'numeric' });
  const month = date.toLocaleDateString(locale, { timeZone: timezone, month: '2-digit' });
  const day = date.toLocaleDateString(locale, { timeZone: timezone, day: '2-digit' });
  
  // Format according to user's preference
  switch (dateFormat) {
    case 'mm/dd/yyyy':
      return `${month}/${day}/${year}`;
    case 'dd/mm/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-mm-dd':
    default:
      return `${year}-${month}-${day}`;
  }
};

/**
 * Formats a date string to full date and time format using centralized settings
 */
export const formatDateTime = (dateInput: any): string => {
  let date = dateInput;
  if (!date) return '';
  if (date instanceof Timestamp) {
    date = date.toDate();
  }
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const timezone = getCurrentTimezone();
  const timeFormat = getCurrentTimeFormat();
  const locale = getLocale();
  return date.toLocaleString(locale, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === 12
  });
};

/**
 * Formats a date string to time only format using centralized settings (24h format)
 */
export const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const timezone = getCurrentTimezone();
  const timeFormat = getCurrentTimeFormat();
  const locale = getLocale();
  
  return date.toLocaleTimeString(locale, { 
    timeZone: timezone,
    hour: '2-digit', 
    minute: '2-digit',
    hour12: timeFormat === 12
  });
};

/**
 * Formats a date string to a relative time format (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateString);
  }
};

/**
 * Groups items by date in East Africa Time
 */
export const groupByDate = <T extends { timestamp?: string; created_at?: string; requestTimestamp?: string }>(
  items: T[]
): { [date: string]: T[] } => {
  const grouped: { [date: string]: T[] } = {};
  
  items.forEach(item => {
    const dateString = item.timestamp || item.created_at || item.requestTimestamp;
    if (dateString) {
      const date = formatDate(dateString);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(item);
    }
  });
  
  return grouped;
};

/**
 * Gets current timestamp in ISO format (UTC, but will be displayed in EAT)
 */
export const getCurrentTimestamp = (): string => {
  return new Date().toISOString();
};

/**
 * Gets current date and time in East Africa Time
 */
export const getCurrentDateTimeInTimezone = (): string => {
  const now = new Date();
  return formatDateTime(now.toISOString());
};

/**
 * Gets current date in East Africa Time
 */
export const getCurrentDateInTimezone = (): string => {
  const now = new Date();
  return formatDate(now.toISOString());
};

/**
 * Checks if a date is today using centralized timezone settings
 */
export const isToday = (dateString: string): boolean => {
  const inputDate = new Date(dateString);
  const today = new Date();
  const timezone = getCurrentTimezone();
  
  const inputDateInTimezone = inputDate.toLocaleDateString('en-CA', { timeZone: timezone });
  const todayInTimezone = today.toLocaleDateString('en-CA', { timeZone: timezone });
  
  return inputDateInTimezone === todayInTimezone;
};

/**
 * Checks if a date is yesterday using centralized timezone settings
 */
export const isYesterday = (dateString: string): boolean => {
  const inputDate = new Date(dateString);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const timezone = getCurrentTimezone();
  
  const inputDateInTimezone = inputDate.toLocaleDateString('en-CA', { timeZone: timezone });
  const yesterdayInTimezone = yesterday.toLocaleDateString('en-CA', { timeZone: timezone });
  
  return inputDateInTimezone === yesterdayInTimezone;
};

/**
 * Formats date for display with "Today", "Yesterday", or date
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (isToday(dateString)) {
    return 'Today';
  } else if (isYesterday(dateString)) {
    return 'Yesterday';
  } else {
    return formatDate(dateString);
  }
};

/**
 * Validates date input
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Gets date input value format (YYYY-MM-DD)
 */
export const getDateInputValue = (date?: Date): string => {
  const d = date || new Date();
  return d.toISOString().split('T')[0];
};

/**
 * Gets time input value format (HH:MM)
 */
export const getTimeInputValue = (date?: Date): string => {
  const d = date || new Date();
  return d.toTimeString().slice(0, 5);
};

/**
 * Formats a date string to YYYY-MM-DD format specifically for HTML input fields and system use
 * This bypasses user settings since HTML date inputs require ISO format
 */
export const formatDateForInput = (dateInput: any): string => {
  let date = dateInput;
  if (!date) return '';
  if (date instanceof Timestamp) {
    date = date.toDate();
  }
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const timezone = getCurrentTimezone();
  const locale = getLocale();
  
  const year = date.toLocaleDateString(locale, { timeZone: timezone, year: 'numeric' });
  const month = date.toLocaleDateString(locale, { timeZone: timezone, month: '2-digit' });
  const day = date.toLocaleDateString(locale, { timeZone: timezone, day: '2-digit' });
  
  // Always return YYYY-MM-DD for input fields
  return `${year}-${month}-${day}`;
};

 